import { create } from "zustand";
import { Blackboard } from "../engine/Blackboard";
import { EventBus, type ChoiceOption } from "../engine/EventBus";
import { Interpreter, type InterpreterState, type VnProgram } from "../engine/Interpreter";
import { GrantLoader, type GrantPayloadLike } from "../engine/GrantLoader";
import { urlParamsToGrantDefs, urlParamsToPayload, type UrlParamDef } from "../engine/UrlParams";
import { SandboxEngine, type RoomState, type InventoryItem } from "../engine/SandboxEngine";
import { transitionRoom, resolveBackground } from "../engine/transitionRoom";
import type { BacklogEntry } from "../components/Backlog";
import type { PopupItem } from "../components/PopupNotification";
import { platform } from "../platform";

type Screen = "title" | "playing";
type Overlay = "none" | "pause" | "save" | "load" | "settings" | "backlog";

interface GameState {
  screen: Screen;
  overlay: Overlay;

  status: InterpreterState;
  dialogue: string | null;
  speaker: string | null;
  speakerColor: string | null;
  choices: ChoiceOption[] | null;
  ended: boolean;

  backlog: BacklogEntry[];
  skipMode: boolean;
  autoMode: boolean;
  skipSeenMode: boolean;
  currentBeatId: string | null;
  hudHidden: boolean;

  videoAsset: string | null;
  videoAutoplay: boolean;
  videoSkipAfter: number | undefined;

  popupQueue: PopupItem[];
  achievementUnlocked: Set<string>;

  minigameId: string | null;

  sandboxActive: boolean;
  sandboxRoom: RoomState | null;
  sandboxNarrationQueue: string[];
  sandboxEngine: SandboxEngine | null;
  sandboxInventory: InventoryItem[];
  sandboxCapacity: { used: number; max: number };
  sandboxAssembleMode: boolean;
  sandboxAssembleFirst: string | null;
  sandboxGiveMode: boolean;
  sandboxGiveItem: string | null;
  /** Formatted clock readout for the HUD; null when the game has no calendar. */
  sandboxTime: string | null;

  bb: Blackboard;
  bus: EventBus;
  interpreter: Interpreter;

  setScreen: (screen: Screen) => void;
  setOverlay: (overlay: Overlay) => void;

  loadProgram: (program: VnProgram, gameId: string, urlParams?: UrlParamDef[]) => void;
  advance: () => void;
  selectChoice: (index: number) => void;

  setSkipMode: (on: boolean) => void;
  toggleAutoMode: () => void;
  toggleSkipSeenMode: () => void;
  setSkipSeenMode: (on: boolean) => void;
  setHudHidden: (on: boolean) => void;

  endVideo: () => void;
  dismissPopup: (id: number) => void;
  returnToTitle: () => void;

  sandboxMove: (targetPlaceId: string) => void;
  sandboxDispatch: (entityId: string, verb: string) => void;
  sandboxDismissNarration: () => void;
  sandboxStartAssemble: (itemId: string) => void;
  sandboxCancelAssemble: () => void;
  sandboxCompleteAssemble: (secondItemId: string) => void;
  sandboxCancelGive: () => void;
  sandboxCompleteGive: (targetCharId: string) => void;
  sandboxWait: (minutes: number) => void;
  sandboxWaitUntil: (hour: number, minute: number) => void;
}

export const useGameStore = create<GameState>((set, get) => {
  const bb = new Blackboard();
  const bus = new EventBus();
  const interpreter = new Interpreter(bb, bus);
  interpreter.setDeferredStart(true);

  bus.on("dialogue.narrate", ({ resolved, id }) => {
    set((s) => ({
      dialogue: resolved,
      speaker: null,
      speakerColor: null,
      status: "waiting",
      choices: null,
      currentBeatId: id,
      backlog: [...s.backlog, { speaker: null, text: resolved }],
    }));
  });

  bus.on("dialogue.say", ({ who, resolved, color, id }) => {
    const entry: BacklogEntry = { speaker: who, text: resolved };
    if (color) entry.speakerColor = color;
    set((s) => ({
      dialogue: resolved,
      speaker: who,
      speakerColor: color ?? null,
      status: "waiting",
      choices: null,
      currentBeatId: id,
      backlog: [...s.backlog, entry],
    }));
  });

  bus.on("choice.show", ({ options }) => {
    set({ choices: options, status: "choosing", dialogue: null, speaker: null });
  });

  bus.on("video.play", ({ asset, autoplay, skip_after }) => {
    set({
      videoAsset: asset,
      videoAutoplay: autoplay ?? true,
      videoSkipAfter: skip_after,
      status: "waiting",
      dialogue: null,
      speaker: null,
      choices: null,
    });
  });

  let _popupId = 0;
  bus.on("popup.show", ({ text, icon, duration }) => {
    const id = ++_popupId;
    set((s) => ({
      popupQueue: [...s.popupQueue, { id, text, icon, duration }],
    }));
  });

  bus.on("achievement.unlock", ({ id, title, icon }) => {
    const popupId = ++_popupId;
    set((s) => ({
      achievementUnlocked: new Set([...s.achievementUnlocked, id]),
      popupQueue: [...s.popupQueue, {
        id: popupId,
        text: title,
        icon,
        duration: 5,
        header: "Achievement Unlocked",
      }],
    }));
  });

  bus.on("story.end", () => {
    set({ ended: true, status: "ended", choices: null, skipMode: false, autoMode: false, skipSeenMode: false, currentBeatId: null, hudHidden: false, videoAsset: null, minigameId: null, sandboxActive: false, sandboxNarrationQueue: [], sandboxInventory: [], sandboxCapacity: { used: 0, max: 8 }, sandboxAssembleMode: false, sandboxAssembleFirst: null, sandboxGiveMode: false, sandboxGiveItem: null });
  });

  bus.on("minigame.start", ({ id }) => {
    set({ status: "minigame", dialogue: null, speaker: null, choices: null, minigameId: id });
  });

  bus.on("minigame.complete", ({ result, delta }) => {
    interpreter.exitMinigame(result, delta);
    set({ minigameId: null });
  });

  // Tracks the asset currently shown for the sandbox room, so a phase flip
  // (time.changed) only re-emits the background when it actually changes (v5).
  let _lastSandboxBg: string | undefined;

  bus.on("sandbox.enter", () => {
    const program = interpreter.getProgram();
    const entities = program?.entities ?? { characters: {}, places: {}, objects: {} };
    const defaultShelfScale = (program?.meta as { display?: { shelf_scale?: number } } | undefined)?.display?.shelf_scale;
    const engine = new SandboxEngine(entities, bb, bus, defaultShelfScale);
    const globals = program?.globals;
    engine.loadRules(globals?.rules ?? []);
    engine.loadTriggers(globals?.triggers ?? []);
    engine.loadRecipes(globals?.recipes ?? []);
    engine.loadFunctions(globals?.functions ?? []);
    engine.setGameClock(interpreter.gameClock);
    engine.setExitCallback((scene) => interpreter.exitSandbox(scene));
    const room = engine.getCurrentRoom();
    if (room) {
      const phase = interpreter.gameClock?.partOfDay();
      transitionRoom(entities, bus, room.id, undefined, phase);
      const place = entities.places[room.id];
      _lastSandboxBg = resolveBackground(place?.background, place?.image, phase);
    }
    set({
      sandboxActive: true,
      sandboxEngine: engine,
      sandboxRoom: room,
      sandboxNarrationQueue: [],
      sandboxInventory: engine.getInventory(),
      sandboxCapacity: engine.getCapacity(),
      sandboxAssembleMode: false,
      sandboxAssembleFirst: null,
      sandboxGiveMode: false,
      sandboxGiveItem: null,
      sandboxTime: interpreter.gameClock?.formatted() ?? null,
      status: "sandbox",
      dialogue: null,
      speaker: null,
      choices: null,
    });
    engine.emitRoomObjects();
  });

  bus.on("sandbox.exit", () => {
    set({ sandboxActive: false, sandboxEngine: null, sandboxRoom: null, sandboxNarrationQueue: [], sandboxInventory: [], sandboxCapacity: { used: 0, max: 8 }, sandboxAssembleMode: false, sandboxAssembleFirst: null, sandboxGiveMode: false, sandboxGiveItem: null });
  });

  bus.on("sandbox.move", ({ to }) => {
    const engine = get().sandboxEngine;
    if (engine) {
      const room = engine.getRoom(to);
      const targetName = room?.name ?? to;
      const program = interpreter.getProgram();
      const entities = program?.entities ?? { characters: {}, places: {}, objects: {} };
      const prevRoom = get().sandboxRoom;
      const phase = interpreter.gameClock?.partOfDay();
      transitionRoom(entities, bus, to, prevRoom?.characters, phase);
      const place = entities.places[to];
      _lastSandboxBg = resolveBackground(place?.background, place?.image, phase);
      set((s) => ({
        sandboxRoom: room,
        sandboxNarrationQueue: [...s.sandboxNarrationQueue, `You head to ${targetName}.`],
      }));
      engine.emitRoomObjects();
    }
  });

  bus.on("sandbox.narrate", ({ text }) => {
    set((s) => ({ sandboxNarrationQueue: [...s.sandboxNarrationQueue, text] }));
  });

  // The game clock advanced — refresh the HUD readout, and re-resolve a
  // phase-keyed room background when the time-of-day phase flips (v5).
  bus.on("time.changed", ({ display, partOfDay }) => {
    set({ sandboxTime: display });
    const s = get();
    if (!s.sandboxActive || !s.sandboxRoom) return;
    const place = interpreter.getProgram()?.entities?.places?.[s.sandboxRoom.id];
    if (!place) return;
    const asset = resolveBackground(place.background, place.image, partOfDay);
    if (asset && asset !== _lastSandboxBg) {
      bus.emit("bg.set", { asset, transition: "dissolve", duration: 0.8, place_ref: s.sandboxRoom.id });
      _lastSandboxBg = asset;
    }
  });

  // Rollback restore is silent (Blackboard.restore fires no listeners), so the
  // clock won't emit time.changed on rewind — re-read it directly.
  bus.on("rollback.done", () => {
    set({ sandboxTime: interpreter.gameClock?.formatted() ?? null });
  });

  bus.on("sandbox.inventory", () => {
    const engine = get().sandboxEngine;
    if (engine) {
      const room = engine.getCurrentRoom();
      set({
        sandboxInventory: engine.getInventory(),
        sandboxCapacity: engine.getCapacity(),
        sandboxRoom: room,
      });
      engine.emitRoomObjects(); // re-place the room after a take/drop/assemble
    }
  });

  function syncModeState() {
    const engine = get().sandboxEngine;
    if (!engine) return;
    set({
      sandboxAssembleMode: engine.assembleMode,
      sandboxAssembleFirst: engine.assembleFirst,
      sandboxGiveMode: engine.giveMode,
      sandboxGiveItem: engine.giveItem,
    });
  }

  return {
    screen: "title",
    overlay: "none",

    status: "idle",
    dialogue: null,
    speaker: null,
    speakerColor: null,
    choices: null,
    ended: false,

    backlog: [],
    skipMode: false,
    autoMode: false,
    skipSeenMode: false,
    currentBeatId: null,
    hudHidden: false,

    videoAsset: null,
    videoAutoplay: true,
    videoSkipAfter: undefined,

    popupQueue: [],
    achievementUnlocked: new Set<string>(),

    minigameId: null,

    sandboxActive: false,
    sandboxRoom: null,
    sandboxNarrationQueue: [],
    sandboxEngine: null,
    sandboxInventory: [],
    sandboxCapacity: { used: 0, max: 8 },
    sandboxAssembleMode: false,
    sandboxAssembleFirst: null,
    sandboxGiveMode: false,
    sandboxGiveItem: null,
    sandboxTime: null,

    bb,
    bus,
    interpreter,

    setScreen(screen) {
      set({ screen });
    },

    setOverlay(overlay) {
      set({ overlay });
    },

    loadProgram(program: VnProgram, gameId: string, urlParams: UrlParamDef[] = []) {
      bus.emit("game.reset");
      bb.clear();
      set({
        dialogue: null,
        speaker: null,
        speakerColor: null,
        choices: null,
        ended: false,
        status: "running",
        screen: "playing",
        overlay: "none",
        backlog: [],
        skipMode: false,
        autoMode: false,
        skipSeenMode: false,
        videoAsset: null,
        videoSkipAfter: undefined,
        popupQueue: [],
      });
      interpreter.setAchievementSink(platform.achievementSink());
      // Grants only matter for a fresh start (they override VAR decl defaults
      // as the entry scene's decl beats execute) — loading a save restores the
      // blackboard directly and never re-runs this path. url_params (req
      // Module 12.3 — a platform-level feature, not Prelude-specific) merge
      // into the exact same declared-list/payload GrantLoader already
      // resolves, per the req doc's own "same mechanism as session grants".
      platform
        .fetchGrants(gameId)
        .then((payload) => {
          const declared = [...(program.globals?.grants ?? []), ...urlParamsToGrantDefs(urlParams)];
          const urlPayload = urlParamsToPayload(urlParams, new URLSearchParams(window.location.search));
          const merged: GrantPayloadLike = {
            grants: { ...(payload?.grants ?? {}), ...urlPayload.grants },
            expires: payload?.expires ?? null,
          };
          interpreter.setGrantLoader(GrantLoader.from(declared, merged));
        })
        .catch((err) => {
          // Grants are best-effort (T3) — a broken merge must not strand the
          // game on a blank screen, since `screen: "playing"` above already
          // mounted Phaser with nothing loaded into it.
          console.error("Failed to resolve grants; starting with declared defaults", err);
          interpreter.setGrantLoader(GrantLoader.empty());
        })
        .finally(() => {
          interpreter.load(program, gameId);
        });
    },

    advance() {
      // Dismiss an active featured presentation BEFORE running the next beats, so a
      // presentation created by *this* advance survives until the *next* click.
      bus.emit("advance");
      interpreter.advance();
    },

    selectChoice(index: number) {
      interpreter.selectChoice(index);
    },

    setSkipMode(on: boolean) {
      set({ skipMode: on, autoMode: on ? false : get().autoMode });
    },

    toggleAutoMode() {
      set((s) => ({ autoMode: !s.autoMode, skipMode: false }));
    },

    toggleSkipSeenMode() {
      set((s) => ({ skipSeenMode: !s.skipSeenMode }));
    },

    setSkipSeenMode(on: boolean) {
      set({ skipSeenMode: on });
    },

    setHudHidden(on: boolean) {
      set({ hudHidden: on });
    },

    endVideo() {
      set({ videoAsset: null, videoSkipAfter: undefined });
      bus.emit("video.end");
    },

    dismissPopup(id: number) {
      set((s) => ({
        popupQueue: s.popupQueue.filter((p) => p.id !== id),
      }));
    },

    returnToTitle() {
      interpreter.cancelWait();
      set({
        screen: "title",
        overlay: "none",
        dialogue: null,
        speaker: null,
        speakerColor: null,
        choices: null,
        ended: false,
        status: "idle",
        backlog: [],
        skipMode: false,
        autoMode: false,
        videoAsset: null,
        videoSkipAfter: undefined,
        popupQueue: [],
        minigameId: null,
        sandboxActive: false,
        sandboxRoom: null,
        sandboxNarrationQueue: [],
        sandboxEngine: null,
        sandboxInventory: [],
        sandboxCapacity: { used: 0, max: 8 },
        sandboxAssembleMode: false,
        sandboxAssembleFirst: null,
        sandboxGiveMode: false,
        sandboxGiveItem: null,
        sandboxTime: null,
      });
    },

    sandboxMove(targetPlaceId: string) {
      const engine = get().sandboxEngine;
      if (engine) {
        engine.move(targetPlaceId);
      }
    },

    sandboxDispatch(entityId: string, verb: string) {
      const engine = get().sandboxEngine;
      if (!engine) return;
      engine.dispatch(entityId, verb);
      syncModeState();
    },

    sandboxWait(minutes: number) {
      get().sandboxEngine?.wait(minutes);
    },

    sandboxWaitUntil(hour: number, minute: number) {
      get().sandboxEngine?.waitUntil(hour, minute);
    },

    sandboxDismissNarration() {
      set((s) => ({ sandboxNarrationQueue: s.sandboxNarrationQueue.slice(1) }));
      bus.emit("advance"); // dismissing the caption ends an active featured presentation
    },

    sandboxStartAssemble(itemId: string) {
      const engine = get().sandboxEngine;
      if (!engine) return;
      engine.startAssemble(itemId);
      syncModeState();
    },

    sandboxCancelAssemble() {
      const engine = get().sandboxEngine;
      if (!engine) return;
      engine.cancelAssemble();
      syncModeState();
    },

    sandboxCompleteAssemble(secondItemId: string) {
      const engine = get().sandboxEngine;
      if (!engine) return;
      engine.completeAssemble(secondItemId);
      syncModeState();
    },

    sandboxCancelGive() {
      const engine = get().sandboxEngine;
      if (!engine) return;
      engine.cancelGive();
      syncModeState();
    },

    sandboxCompleteGive(targetCharId: string) {
      const engine = get().sandboxEngine;
      if (!engine) return;
      engine.completeGive(targetCharId);
      syncModeState();
    },
  };
});
